/**
 * EnvironmentBanner — Umgebungs-Erkennung + Build-Alter-Warnung
 *
 * Zeigt Banner basierend auf Hostname:
 * - Prod (libero-anstoss24.de): Kein Banner
 * - Staging (dev.libero-anstoss24.de): Gelber Banner, ROT wenn Build > 2h alt
 * - Dev (<name>.libero-anstoss24.de): Farbiger Banner pro Entwickler
 * - Localhost: Grauer Banner
 *
 * Multi-Claude-Safety (2026-05-26):
 * Banner pollt /api/system/version (public). Wenn der Backend-Build (Build-Zeit)
 * aelter als 2h ist UND Stage/Prod-Domain → Banner wird ROT mit Hinweis dass
 * Deploys evtl. haengen. Klick auf Banner oeffnet GitHub-Actions-Seite.
 *
 * Exportiert außerdem:
 * - getEnvType(): aktuellen Env-Typ ('prod' | 'staging' | 'matze' | ...)
 * - getEnvColor(): CI-Farbe der aktuellen Instanz (Hex)
 */

import { useEffect, useState } from 'react';

const ENV_CONFIG: Record<string, { color: string; name: string; title: string }> = {
  prod:    { color: '#E9FF57', name: 'Prod',    title: 'Libero — anstoss24' },
  staging: { color: '#FF9500', name: 'Staging', title: '[STAGE] Libero — anstoss24' },
  matze:   { color: '#3B82F6', name: 'Matze',   title: '[Matze] Libero — anstoss24' },
  max:     { color: '#10B981', name: 'Max',     title: '[Max] Libero — anstoss24' },
  maxi:    { color: '#8B5CF6', name: 'Maxi',    title: '[Maxi] Libero — anstoss24' },
  simon:   { color: '#F59E0B', name: 'Simon',   title: '[Simon] Libero — anstoss24' },
  fabian:  { color: '#EC4899', name: 'Fabian',  title: '[Fabian] Libero — anstoss24' },
};

// Build-Alter-Threshold (in Stunden) ab wann der Banner rot wird
const STALE_BUILD_THRESHOLD_HOURS = 2;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // alle 5min

export function getEnvType(): string {
  if (typeof window === 'undefined') return 'prod';
  const hostname = window.location.hostname;
  if (hostname === 'libero-anstoss24.de') return 'prod';
  if (hostname === 'dev.libero-anstoss24.de') return 'staging';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'prod';
  const match = hostname.match(/^([a-z]+)\.libero-anstoss24\.de$/);
  if (match && ENV_CONFIG[match[1]]) return match[1];
  return 'prod';
}

export function getEnvColor(): string {
  return ENV_CONFIG[getEnvType()]?.color ?? '#E9FF57';
}

export function useEnvironmentStatus() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'libero-anstoss24.de') {
    return { isProd: true, isVisible: false };
  }

  return { isProd: false, isVisible: true };
}

/** Liefert das Alter des aktuellen Backend-Builds in Minuten — null wenn unbekannt. */
function useBuildAgeMinutes(): number | null {
  const [ageMin, setAgeMin] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch('/api/system/version', { credentials: 'omit' });
        if (!res.ok) return;
        const data = await res.json();
        const buildTimeStr = data?.data?.backend?.buildTime;
        if (!buildTimeStr) return;
        const buildTime = new Date(buildTimeStr).getTime();
        if (Number.isNaN(buildTime)) return;
        const minutes = Math.floor((Date.now() - buildTime) / 60000);
        if (!cancelled) setAgeMin(minutes);
      } catch {
        // Silent — kein Build-Info ist kein Banner-Block
      }
    }

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return ageMin;
}

function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h${m}min`;
}

function EnvironmentBanner() {
  const envType = getEnvType();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const buildAgeMin = useBuildAgeMinutes();

  // Set document.title and favicon link based on env
  useEffect(() => {
    const config = ENV_CONFIG[envType];
    if (config) {
      document.title = config.title;
    }

    // Favicon tauschen — Cache-Buster erzwingt Neu-Laden nach Deploy
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (link) {
      link.setAttribute('href', `/favicon-${envType}.svg?v=3`);
    }
  }, [envType]);

  // Kein Rendering bei SSR
  if (typeof window === 'undefined') return null;

  // Soll der Banner als "Build stale" rot werden?
  const isStaleBuild =
    buildAgeMin !== null && buildAgeMin > STALE_BUILD_THRESHOLD_HOURS * 60;

  const StaleHint = isStaleBuild && buildAgeMin !== null ? (
    <a
      href="https://github.com/anstoss24/libero/actions"
      target="_blank"
      rel="noopener"
      className="ml-3 underline text-white/90 hover:text-white"
      title="Wenn dazwischen PRs gemerged wurden, hängt der Deploy evtl. — Logs öffnen"
    >
      ⚠ Build vor {formatAge(buildAgeMin)} — Deploy-Logs prüfen
    </a>
  ) : null;

  const BuildHint = !isStaleBuild && buildAgeMin !== null ? (
    <span className="ml-3 text-white/70 text-xs">Build vor {formatAge(buildAgeMin)}</span>
  ) : null;

  // PROD: Kein Banner (auch nicht bei stale)
  if (hostname === 'libero-anstoss24.de') {
    return null;
  }

  // STAGING: Gelber Banner — rot wenn Build > 2h
  if (hostname === 'dev.libero-anstoss24.de') {
    return (
      <div
        className="fixed top-0 left-0 right-0 h-6 flex items-center justify-center z-50"
        style={{ backgroundColor: isStaleBuild ? '#DC2626' : '#EAB308' }}
      >
        <span className="text-sm font-semibold text-white">STAGING</span>
        {StaleHint}
        {BuildHint}
      </div>
    );
  }

  // LOCALHOST: Grauer Banner
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return (
      <div className="fixed top-0 left-0 right-0 h-6 bg-gray-500 flex items-center justify-center z-50">
        <span className="text-sm font-semibold text-white">LOKAL</span>
      </div>
    );
  }

  // DEV-INSTANZEN: Farbiger Banner pro Entwickler — rot-Warnung wenn stale
  // Format: <name>.libero-anstoss24.de
  const match = hostname.match(/^([a-z]+)\.libero-anstoss24\.de$/);
  if (match) {
    const devName = match[1];
    const devConfig = ENV_CONFIG[devName];

    if (devConfig) {
      return (
        <div
          className="fixed top-0 left-0 right-0 h-6 flex items-center justify-center z-50"
          style={{ backgroundColor: isStaleBuild ? '#DC2626' : devConfig.color }}
        >
          <span className="text-sm font-semibold text-white">DEV — {devConfig.name}</span>
          {StaleHint}
          {BuildHint}
        </div>
      );
    }
  }

  // Unbekannte Instanz: Grauer Banner mit Hostname
  return (
    <div className="fixed top-0 left-0 right-0 h-6 bg-gray-400 flex items-center justify-center z-50">
      <span className="text-sm font-semibold text-white">UNBEKANNT: {hostname}</span>
    </div>
  );
}

export default EnvironmentBanner;
